import { useState } from 'react';
import CollapseIcon from '@/icons/CollapseIcon';
import MenuItem from './MenuItem/MenuItem';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import GetHelpIcon from '@/icons/GetHelpIcon';
import ProjectIcon from '@/icons/ProjectIcon';
import { motion } from 'framer-motion';
import Insights from '../../../icons/files/Insights';
import useAuthStore from '../../../store/Auth';
import useActionStore from '@/store/Actions';
import Banner from './Banner/Banner';
import {
  CrafterLogo3,
  CrafterLogoMark,
  SettingsIcon,
} from '../../../icons/svgs';
import {
  IntegrationsIcon,
  PlansIcon,
  TeamsIcon,
} from '../../../icons/sidebar/sidebarIcons';
import Users from '../../../icons/Users';
import BackIcon from '../../../icons/BackIcon';
import LogoutIcon from '../../../icons/LogoutIcon';
import CreditIndicator from '../CreditIndicator/CreditIndicator';
import Check from '@/icons/Check';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  setConfirmLogout?: (v: boolean) => void;
}

export default function Sidebar({ collapsed, setCollapsed }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, organization, user, creditMessage } = useAuthStore();
  const { toggleLogoutModal } = useActionStore();

  // Track which panel is visible: 'main' or 'settings'
  const isOnSettingsPage = location.pathname.startsWith('/settings');
  const [showSettings, setShowSettings] = useState(isOnSettingsPage);

  const menuItems = [
    { title: 'Insights', icon: Insights, link: '/', isNew: true },
    { title: 'Projects', icon: ProjectIcon, link: '/projects' },
    { title: 'Teams', icon: TeamsIcon, link: '/teams' },
    // ...(isPilot && isAdmin
    //   ? [
    //       {
    //         title: 'Permissions',
    //         icon: UserManagementIcon,
    //         link: '/permissions',
    //       },
    //     ]
    //   : []),
  ];

  const utilityItems = [
    {
      title: 'Get Help',
      icon: GetHelpIcon,
      link: 'https://crafterai.gitbook.io/',
      target: '_blank',
    },
  ];

  const settingsMenuItems = [
    {
      title: 'Profile',
      icon: Users,
      link: '/settings/personal-information',
    },
    { title: 'Plans', icon: PlansIcon, link: '/plans' },
    ...(isAdmin
      ? [
          {
            title: 'Integrations',
            icon: IntegrationsIcon,
            link: '/settings/integrations',
          },
          {
            title: 'Permissions',
            icon: TeamsIcon,
            link: '/settings/permissions',
          },
        ]
      : []),
    // {
    //   title: 'Preferences',
    //   icon: SettingsIcon,
    //   link: '/settings/preferences',
    // },
  ];

  const handleSettingsClick = () => {
    setShowSettings(true);
    navigate('/settings');
  };

  const handleBackClick = () => {
    setShowSettings(false);
    navigate('/');
  };

  return (
    <nav
      className={`h-full w-full flex flex-col gap-3 pt-[10px] pb-[20px] px-1 bg-navigation-side overflow-hidden`}
    >
      <div
        className={`py-4 flex  gap-4 w-full ${collapsed ? 'justify-center items-center flex-col px-1.5' : 'justify-between items-center flex-row px-3'}`}
      >
        {!collapsed && (
          <div className="flex flex-col items-start gap-2">
            <Link
              to={'/'}
              onClick={() => setShowSettings(false)}
              className="w-[140px]"
            >
              <CrafterLogo3 />
            </Link>
          </div>
        )}
        {collapsed && (
          <div className="flex flex-col items-center gap-4 w-full">
            <Link
              to={'/'}
              onClick={() => setShowSettings(false)}
              className="w-[30px]"
            >
              <CrafterLogoMark />
            </Link>
          </div>
        )}
        <div className="flex">
          <div className="w-[50px] py-2 items-center justify-center hidden lg:flex hover:opacity-80 transition-all duration-300 ease-in-out">
            <CollapseIcon
              collapsed={collapsed}
              onClick={() => setCollapsed(!collapsed)}
            />
          </div>
        </div>
      </div>

      {/* Sliding container — both panels sit side by side, we translate the wrapper */}
      <div className="w-full h-full relative overflow-hidden mt-16">
        <div
          className="flex h-full transition-transform duration-200 ease-in-out"
          style={{
            transform: showSettings ? 'translateX(-100%)' : 'translateX(0)',
          }}
        >
          {/* ────── MAIN PANEL ────── */}
          <div className="min-w-full h-full flex flex-col overflow-auto">
            <ul className="w-full flex flex-col gap-2">
              {menuItems.map((menuItem, index) => {
                const isActive =
                  menuItem.link === '/'
                    ? location.pathname === '/' ||
                      location.pathname === '/insights' ||
                      location.pathname.startsWith('/insights/')
                    : location.pathname.startsWith(menuItem.link);
                return (
                  <li key={index}>
                    <MenuItem
                      title={menuItem.title}
                      icon={menuItem.icon}
                      link={menuItem.link}
                      isNew={menuItem.isNew}
                      collapsed={collapsed}
                      active={isActive}
                      onClick={() => navigate(menuItem.link)}
                    />
                  </li>
                );
              })}
            </ul>

            <ul className="w-full flex flex-col gap-2">
              {/* {utilityItems.map((utilityItem, index) => {
                  const isActive = location.pathname.startsWith(
                    utilityItem.link
                  );
                  return (
                    <li key={index}>
                      <MenuItem
                        title={utilityItem.title}
                        icon={utilityItem.icon}
                        link={utilityItem.link}
                        target={utilityItem.target}
                        collapsed={collapsed}
                        active={isActive}
                        onClick={() => navigate(utilityItem.link)}
                      />
                    </li>
                  );
                })} */}

              {/* Settings trigger — opens the settings panel */}
              <li className="mt-2">
                <button
                  onClick={handleSettingsClick}
                  className={`w-full px-3 py-2 flex items-center gap-2 rounded-sm relative hover:bg-navigation-tab-hover overflow-hidden ${
                    isOnSettingsPage
                      ? 'bg-navigation-tab-active text-text-primary-heading'
                      : 'text-text-primary-body'
                  }`}
                >
                  {isOnSettingsPage && (
                    <div className="w-[2px] bg-primary absolute left-0 top-0 h-full" />
                  )}
                  <div className="w-[50px] py-2 flex items-center justify-center">
                    <SettingsIcon {...({ active: isOnSettingsPage } as any)} />
                  </div>
                  <motion.p
                    initial={false}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{
                      duration: 0.25,
                      ease: 'easeInOut',
                      delay: 0.15,
                    }}
                    className={`overflow-hidden whitespace-nowrap ${collapsed ? 'hidden' : 'block'} ${isOnSettingsPage ? 'font-bold' : ''}`}
                  >
                    Settings
                  </motion.p>
                </button>
              </li>

              <button
                onClick={() => toggleLogoutModal(true)}
                className="w-full px-3 py-2 flex items-center gap-2 rounded-sm text-error-2 hover:bg-navigation-tab-hover transition-all duration-200 mb-4"
              >
                <div className="w-[50px] py-2 flex items-center justify-center">
                  <LogoutIcon />
                </div>
                <motion.p
                  initial={false}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{
                    duration: 0.25,
                    ease: 'easeInOut',
                    delay: 0.15,
                  }}
                  className={`overflow-hidden whitespace-nowrap ${collapsed ? 'hidden' : 'block'}`}
                >
                  Logout
                </motion.p>
              </button>
            </ul>

            <div className="mt-auto flex flex-col gap-2">
              <motion.div
                initial={false}
                animate={{ opacity: collapsed ? 0 : 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.1,
                  ease: 'easeInOut',
                  delay: collapsed ? 0 : 0.4,
                }}
                className="mb-2"
              >
                <Banner />
              </motion.div>

              <div
                className={`flex flex-row items-center w-full gap-3 ${collapsed ? 'px-1.5 justify-center' : 'px-3'}`}
              >
                <span className="nameSpan flex items-center justify-center font-semibold text-3xl text-white-1 w-[40px] h-[40px] rounded-full border border-gray-1">
                  {user?.first_name?.charAt(0).toUpperCase() ||
                    user?.email?.charAt(0).toUpperCase() ||
                    'U'}
                </span>

                {!collapsed && (
                  <div>
                    <h3 className="text-text-primary-heading font-semibold lg:text-[16px] xl:text-[16px] 2xl:text-[16px]">
                      {user?.email}
                    </h3>

                    {organization?.pilot ? (
                      <div>
                        <h1 className="text-text-semantics-link font-bold text-[14px] whitespace-nowrap">
                          🚀 Pilot
                        </h1>
                      </div>
                    ) : (
                      <div className="flex flex-row items-center text-text-semantics-link">
                        <Check />
                        <h1 className="font-semibold text-sm">(Free)</h1>
                      </div>
                    )}
                    {/* <p className=" text-text-primary-body font-semibold lg:text-[12px] xl:text-[12px] 2xl:text-[14px]">
                      {organization?.name}
                    </p> */}
                  </div>
                )}

                {/* ======== Credit indicator ===== */}

                {creditMessage !== 'OpenAI quota not exceeded.' &&
                  creditMessage !== 'API call failed: 401' &&
                  creditMessage !== '' &&
                  !collapsed && <CreditIndicator />}
              </div>
            </div>
          </div>

          {/* ────── SETTINGS PANEL ────── */}
          <div className="min-w-full h-full flex flex-col gap-6 overflow-auto">
            {/* Back button */}
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 px-3 py-2 rounded-sm text-text-primary-body hover:bg-navigation-tab-hover transition-all duration-200"
            >
              <div className="w-[25px] py-2 flex items-center justify-center">
                <BackIcon />
              </div>
              <motion.p
                initial={false}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{
                  duration: 0.25,
                  ease: 'easeInOut',
                  delay: 0.15,
                }}
                className={`overflow-hidden whitespace-nowrap font-normal ${collapsed ? 'hidden' : 'block'}`}
              >
                Back to app
              </motion.p>
            </button>

            {/* Settings heading */}
            {!collapsed && (
              <div className="px-5">
                <p className="text-xs uppercase tracking-wider text-text-primary-body/50 font-semibold">
                  Settings
                </p>
              </div>
            )}

            {/* Settings menu items */}
            <ul className="w-full flex flex-col gap-2">
              {settingsMenuItems.map((item, index) => {
                const isActive = location.pathname.startsWith(item.link);
                return (
                  <li key={index}>
                    <MenuItem
                      title={item.title}
                      icon={item.icon}
                      link={item.link}
                      collapsed={collapsed}
                      active={isActive}
                      onClick={() => navigate(item.link)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
}
